import { useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { useT } from "../i18n/I18nContext";

declare global {
  interface Window {
    google?: any;
  }
}

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

interface Props {
  /**
   * When true, also try Google's "One Tap" auto-prompt for already-signed-in
   * users. Off by default because (a) most browsers block the popup if it
   * isn't a direct response to a user gesture, and (b) it requires Google to
   * be configured with FedCM-friendly origins.
   */
  oneTap?: boolean;
}

export function GoogleSignIn({ oneTap = false }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const initializedRef = useRef(false);
  const { signInWithGoogleIdToken, error } = useAuth();
  const t = useT();
  const [loading, setLoading] = useState(false);
  const [scriptReady, setScriptReady] = useState(Boolean(window.google?.accounts?.id));

  // Wait for https://accounts.google.com/gsi/client to load (see index.html).
  useEffect(() => {
    if (window.google?.accounts?.id) {
      setScriptReady(true);
      return;
    }
    const handle = window.setInterval(() => {
      if (window.google?.accounts?.id) {
        setScriptReady(true);
        window.clearInterval(handle);
      }
    }, 100);
    return () => window.clearInterval(handle);
  }, []);

  // Keep the latest auth callback in a ref so that initialize() runs once and
  // doesn't trigger Google's "called multiple times" warning.
  const callbackRef = useRef(signInWithGoogleIdToken);
  useEffect(() => {
    callbackRef.current = signInWithGoogleIdToken;
  }, [signInWithGoogleIdToken]);

  useEffect(() => {
    if (!scriptReady || !GOOGLE_CLIENT_ID) return;
    if (initializedRef.current) return;
    initializedRef.current = true;

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      // Auto-select piggy-backs on FedCM and tries to open without a click,
      // which most browsers block. Off unless explicitly asked for.
      auto_select: false,
      ux_mode: "popup",
      callback: async (resp: { credential: string }) => {
        if (!resp?.credential) return;
        setLoading(true);
        try {
          await callbackRef.current(resp.credential);
        } catch {
          // AuthContext already surfaces the error message via `error` below.
        } finally {
          setLoading(false);
        }
      },
    });

    if (containerRef.current) {
      window.google.accounts.id.renderButton(containerRef.current, {
        theme: "filled_blue",
        size: "large",
        text: "continue_with",
        shape: "pill",
        logo_alignment: "left",
      });
    }

    if (oneTap) {
      // Best-effort: if Google can't open the One Tap UI (popup blocked,
      // origin not allowed, FedCM disabled, ...) it just logs and the user
      // can still click the rendered button.
      try {
        window.google.accounts.id.prompt();
      } catch {
        // Ignore — the rendered button is still functional.
      }
    }
  }, [scriptReady, oneTap]);

  if (!GOOGLE_CLIENT_ID || GOOGLE_CLIENT_ID.startsWith("your-google-client-id")) {
    const msg = t("login.google.missing", { file: "frontend/.env" });
    const parts = msg.split("frontend/.env");
    return (
      <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
        {parts[0]}
        <code>frontend/.env</code>
        {parts[1] ?? ""}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3">
      <div ref={containerRef} />
      {loading && <div className="text-xs text-slate-500">{t("login.signingIn")}</div>}
      {error && <div className="text-sm text-red-600">{error}</div>}
    </div>
  );
}
